import { useRef, useEffect, useState, useMemo, useCallback } from "react";
import { cn } from "@utils";

const TokenDropdown = ({
  tokens,
  onSelect,
  onClose,
  caretPos,
  openAbove,
  alignRight,
}) => {
  const [search, setSearch] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const filtered = useMemo(
    () =>
      tokens.filter((t) => t.name.toLowerCase().includes(search.toLowerCase())),
    [tokens, search],
  );

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [search]);

  useEffect(() => {
    const item = listRef.current?.children[activeIndex];
    if (item) item.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onCloseRef.current();
      }
    };
    document.addEventListener("mousedown", handler, true);
    return () => document.removeEventListener("mousedown", handler, true);
  }, []);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i < filtered.length - 1 ? i + 1 : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex((i) => (i > 0 ? i - 1 : filtered.length - 1));
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        if (filtered[activeIndex]) onSelect(filtered[activeIndex]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    },
    [filtered, activeIndex, onSelect, onClose],
  );

  return (
    <div
      ref={dropdownRef}
      className="absolute z-[9999] bg-white border border-zinc-200 rounded-lg shadow-lg w-64 flex flex-col"
      style={{
        top: openAbove ? caretPos.lineTop - 4 : caretPos.top + 4,
        left: caretPos.left,
        transform:
          [openAbove && "translateY(-100%)", alignRight && "translateX(-100%)"]
            .filter(Boolean)
            .join(" ") || undefined,
      }}
    >
      <div className="px-2 pt-2 pb-1">
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search tokens..."
          className="w-full px-2 py-1.5 text-sm border border-zinc-200 rounded-md outline-none focus:border-primary-300 placeholder-zinc-400"
        />
      </div>
      <ul ref={listRef} className="max-h-44 overflow-y-auto py-1">
        {filtered.length === 0 ? (
          <li className="px-3 py-2 text-sm text-zinc-400">No tokens found</li>
        ) : (
          filtered.map((token, i) => (
            <li
              key={token.value}
              className={cn(
                "px-3 py-1.5 cursor-pointer text-sm",
                i === activeIndex
                  ? "bg-primary-50 text-primary-700"
                  : "text-zinc-700 hover:bg-zinc-50",
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect(token);
              }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {token.name}
            </li>
          ))
        )}
      </ul>
    </div>
  );
};

/* ---- Helper: measure caret rect even on empty lines ---- */
function getCaretRect(editorEl) {
  const sel = window.getSelection();
  if (!sel.rangeCount || !editorEl?.contains(sel.anchorNode)) return null;
  const range = sel.getRangeAt(0);
  let rect = range.getBoundingClientRect();
  if (rect.top === 0 && rect.left === 0) {
    const span = document.createElement("span");
    span.textContent = "\u200B";
    range.insertNode(span);
    rect = span.getBoundingClientRect();
    span.remove();
    sel.removeAllRanges();
    sel.addRange(range);
  }
  return rect.top === 0 && rect.left === 0 ? null : rect;
}

/* ---- Idle "+Token" button ---- */
export function TokenIdleButton({
  editorRef,
  containerRef,
  dropdownOpen,
  onActivate,
}) {
  const [pos, setPos] = useState(null);
  const timerRef = useRef(null);
  const dropdownOpenRef = useRef(dropdownOpen);
  dropdownOpenRef.current = dropdownOpen;

  const reset = useCallback(() => {
    setPos(null);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (dropdownOpenRef.current) return;
      const cRect = containerRef.current?.getBoundingClientRect();
      if (!cRect) return;
      const rect = getCaretRect(editorRef.current);
      if (rect) {
        setPos({
          top: rect.bottom - cRect.top,
          left: rect.left - cRect.left,
        });
      }
    }, 200);
  }, [containerRef, editorRef]);

  useEffect(() => {
    return () => clearTimeout(timerRef.current);
  }, []);

  // Hide on scroll, then re-show after idle
  useEffect(() => {
    const handler = () => reset();
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [reset]);

  // Expose reset so parent can call it on input/focus/click
  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.__resetIdleTimer = reset;
    }
  }, [reset, editorRef]);

  // Clear when dropdown opens
  useEffect(() => {
    if (dropdownOpen) {
      setPos(null);
      clearTimeout(timerRef.current);
    }
  }, [dropdownOpen]);

  // Clear when editor loses focus
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    const handler = () => {
      if (dropdownOpenRef.current) return;
      setPos(null);
      clearTimeout(timerRef.current);
    };
    editor.addEventListener("blur", handler);
    return () => editor.removeEventListener("blur", handler);
  }, [editorRef]);

  if (!pos || dropdownOpen) return null;

  return (
    <button
      type="button"
      ref={(el) => {
        if (el) {
          requestAnimationFrame(() => {
            el.style.transition =
              "opacity 200ms ease-out, transform 200ms ease-out";
            el.style.opacity = "1";
            el.style.transform = "translateX(-50%) translateY(0)";
          });
        }
      }}
      className="absolute z-[9998] flex items-center gap-1 bg-white border border-zinc-200 rounded-full shadow-sm px-2 py-0.5 text-xs text-zinc-700 hover:text-primary-600 hover:border-primary-300 cursor-pointer whitespace-nowrap"
      style={{
        top: pos.top + 6,
        left: pos.left,
        opacity: 0,
        transform: "translateX(-50%) translateY(-4px)",
      }}
      onMouseDown={(e) => {
        e.preventDefault();
        setPos(null);
        clearTimeout(timerRef.current);
        onActivate();
      }}
    >
      <span className="text-primary-400 font-bold">+</span>
      Insert Field
    </button>
  );
}

export { getCaretRect };
export default TokenDropdown;
